<template>
  <div class="ac-wrap">

    <!-- ── Header ── -->
    <div class="ac-header">
      <div class="ac-header-left">
        <div class="ac-header-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="3" width="20" height="14" rx="2" stroke="#F5B935" stroke-width="2"/>
            <path d="M8 21h8M12 17v4" stroke="#F5B935" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <div>
          <div class="ac-header-title">Configurações AWS S3</div>
          <div class="ac-header-sub">Gerencie as chaves de acesso ao bucket S3 sem expor no ambiente</div>
        </div>
      </div>
      <button class="ac-btn-new" @click="openModal()">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
        Nova Configuração
      </button>
    </div>

    <!-- ── Info banner ── -->
    <div class="ac-banner">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#F5B935" stroke-width="2"/><path d="M12 16v-4M12 8h.01" stroke="#F5B935" stroke-width="2" stroke-linecap="round"/></svg>
      <span>Apenas a configuração <strong>ativa</strong> é utilizada pelo sistema. A secret key é exibida mascarada — apenas os últimos 4 caracteres são visíveis.</span>
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="ac-loading">
      <div class="ac-spinner"></div>
      <span>Carregando configurações...</span>
    </div>

    <!-- ── Erro ── -->
    <div v-else-if="error" class="ac-error">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#f87171" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="#f87171" stroke-width="2" stroke-linecap="round"/></svg>
      {{ error }}
    </div>

    <!-- ── Tabela ── -->
    <div v-else class="ac-table-wrap">
      <div v-if="!configs.length" class="ac-empty">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="rgba(255,255,255,0.12)" stroke-width="1.5"/><path d="M8 21h8M12 17v4" stroke="rgba(255,255,255,0.12)" stroke-width="1.5" stroke-linecap="round"/></svg>
        <p>Nenhuma configuração cadastrada</p>
        <button class="ac-btn-new ac-btn-new--sm" @click="openModal()">Cadastrar agora</button>
      </div>

      <table v-else class="ac-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Access Key ID</th>
            <th>Secret Key</th>
            <th>Bucket</th>
            <th>Região</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cfg in configs" :key="cfg.id" :class="{ 'ac-row-active': cfg.is_active }">
            <td class="ac-td-label">
              <span class="ac-label-text">{{ cfg.label }}</span>
            </td>
            <td>
              <span class="ac-code">{{ cfg.access_key_id }}</span>
            </td>
            <td>
              <span class="ac-code ac-masked">{{ cfg.secret_access_key_masked }}</span>
            </td>
            <td><span class="ac-badge-info">{{ cfg.bucket_name }}</span></td>
            <td><span class="ac-badge-info">{{ cfg.region }}</span></td>
            <td>
              <span v-if="cfg.is_active" class="ac-badge-active">
                <svg width="7" height="7" viewBox="0 0 8 8"><circle cx="4" cy="4" r="4" fill="#10B981"/></svg>
                Ativa
              </span>
              <span v-else class="ac-badge-inactive">Inativa</span>
            </td>
            <td>
              <div class="ac-actions">
                <button v-if="!cfg.is_active" class="ac-btn-act ac-btn-activate" title="Ativar" @click="activate(cfg.id)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Ativar
                </button>
                <button class="ac-btn-act ac-btn-edit" title="Editar" @click="openModal(cfg)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </button>
                <button v-if="!cfg.is_active" class="ac-btn-act ac-btn-delete" title="Deletar" @click="confirmDelete(cfg)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><polyline points="3,6 5,6 21,6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ══ Modal: criar / editar ══ -->
    <div v-if="modal.open" class="ac-modal-overlay" @click.self="closeModal">
      <div class="ac-modal">
        <div class="ac-modal-header">
          <span>{{ modal.isEdit ? 'Editar Configuração AWS' : 'Nova Configuração AWS' }}</span>
          <button class="ac-modal-close" @click="closeModal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
        </div>

        <form @submit.prevent="saveConfig" class="ac-modal-body">
          <div class="ac-field">
            <label class="ac-label">Label <span class="ac-req">*</span></label>
            <input v-model="form.label" class="ac-input" placeholder="Ex: Produção, Staging..." required />
          </div>

          <div class="ac-field">
            <label class="ac-label">Access Key ID <span class="ac-req">*</span></label>
            <input v-model="form.access_key_id" class="ac-input" placeholder="AKIAIOSFODNN7EXAMPLE" required />
          </div>

          <div class="ac-field">
            <label class="ac-label">Secret Access Key <span class="ac-req">*</span></label>
            <div class="ac-input-pass-wrap">
              <input
                v-model="form.secret_access_key"
                :type="showSecret ? 'text' : 'password'"
                class="ac-input ac-input-pass"
                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                :required="!modal.isEdit"
              />
              <button type="button" class="ac-pass-toggle" @click="showSecret = !showSecret">
                <svg v-if="!showSecret" width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>
                <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M1 1l22 22" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              </button>
            </div>
            <small v-if="modal.isEdit" class="ac-hint">Deixe em branco para manter a chave atual</small>
          </div>

          <div class="ac-row-2">
            <div class="ac-field">
              <label class="ac-label">Bucket Name</label>
              <input v-model="form.bucket_name" class="ac-input" placeholder="dkplayer" />
            </div>
            <div class="ac-field">
              <label class="ac-label">Região</label>
              <div class="ac-select-wrap">
                <select v-model="form.region" class="ac-select">
                  <option value="sa-east-1">sa-east-1 (São Paulo)</option>
                  <option value="us-east-1">us-east-1 (N. Virginia)</option>
                  <option value="us-west-2">us-west-2 (Oregon)</option>
                  <option value="eu-west-1">eu-west-1 (Irlanda)</option>
                </select>
                <svg class="ac-select-arrow" width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="#64748b" stroke-width="2" stroke-linecap="round"/></svg>
              </div>
            </div>
          </div>

          <!-- MediaConvert (HLS) -->
          <div class="ac-section-divider">
            <span>AWS Elemental MediaConvert (Conversão HLS)</span>
            <small>Opcional — habilita conversão para streaming adaptativo</small>
          </div>

          <div class="ac-field">
            <label class="ac-label">MediaConvert Endpoint</label>
            <input v-model="form.mediaconvert_endpoint" class="ac-input" placeholder="https://abc123.mediaconvert.us-east-1.amazonaws.com" />
            <small class="ac-hint">Encontre em: AWS Console → MediaConvert → Account</small>
          </div>

          <div class="ac-field">
            <label class="ac-label">IAM Role ARN</label>
            <input v-model="form.mediaconvert_role_arn" class="ac-input" placeholder="arn:aws:iam::123456789012:role/MediaConvert_Default_Role" />
            <small class="ac-hint">Role com permissão para acessar o S3 e executar MediaConvert</small>
          </div>

          <div class="ac-modal-footer">
            <button type="button" class="ac-btn-cancel" @click="closeModal">Cancelar</button>
            <button type="submit" class="ac-btn-save" :disabled="saving">
              <span v-if="saving" class="ac-spinner-sm"></span>
              {{ saving ? 'Salvando...' : (modal.isEdit ? 'Salvar alterações' : 'Criar configuração') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ══ Modal: confirmar exclusão ══ -->
    <div v-if="deleteTarget" class="ac-modal-overlay" @click.self="deleteTarget = null">
      <div class="ac-modal ac-modal-sm">
        <div class="ac-modal-header">
          <span>Confirmar exclusão</span>
          <button class="ac-modal-close" @click="deleteTarget = null">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="ac-modal-body ac-confirm-body">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="rgba(248,113,113,0.1)"/><path d="M12 8v4M12 16h.01" stroke="#f87171" stroke-width="2" stroke-linecap="round"/></svg>
          <p>Tem certeza que deseja deletar a configuração <strong>{{ deleteTarget.label }}</strong>? Esta ação não pode ser desfeita.</p>
          <div class="ac-confirm-actions">
            <button class="ac-btn-cancel" @click="deleteTarget = null">Cancelar</button>
            <button class="ac-btn-danger" @click="doDelete" :disabled="saving">
              <span v-if="saving" class="ac-spinner-sm"></span>
              {{ saving ? 'Deletando...' : 'Deletar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
const API = `${process.env.VUE_APP_HOST_API_BACKEND || '/api/dkbackend'}/aws-config`

function emptyForm() {
  return { label: '', access_key_id: '', secret_access_key: '', bucket_name: 'dkplayer', region: 'sa-east-1', mediaconvert_endpoint: '', mediaconvert_role_arn: '' }
}

export default {
  name: 'AwsConfig',
  data() {
    return {
      configs:      [],
      loading:      false,
      saving:       false,
      error:        null,
      showSecret:   false,
      deleteTarget: null,
      modal:        { open: false, isEdit: false, editId: null },
      form:         emptyForm(),
    }
  },
  async created() {
    await this.load()
  },
  methods: {
    async load() {
      this.loading = true; this.error = null
      try {
        const res  = await fetch(API)
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Erro ao carregar')
        this.configs = data
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    openModal(cfg = null) {
      this.showSecret = false
      if (cfg) {
        this.modal = { open: true, isEdit: true, editId: cfg.id }
        this.form  = { label: cfg.label, access_key_id: cfg.access_key_id, secret_access_key: '', bucket_name: cfg.bucket_name, region: cfg.region, mediaconvert_endpoint: cfg.mediaconvert_endpoint || '', mediaconvert_role_arn: cfg.mediaconvert_role_arn || '' }
      } else {
        this.modal = { open: true, isEdit: false, editId: null }
        this.form  = emptyForm()
      }
    },

    closeModal() {
      this.modal = { open: false, isEdit: false, editId: null }
    },

    async saveConfig() {
      this.saving = true
      try {
        const body = { ...this.form }
        if (this.modal.isEdit && !body.secret_access_key) delete body.secret_access_key

        const url    = this.modal.isEdit ? `${API}/${this.modal.editId}` : API
        const method = this.modal.isEdit ? 'PUT' : 'POST'
        const res    = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
        const data   = await res.json()
        if (!res.ok) throw new Error(data.error || 'Erro ao salvar')
        this.$message({ type: 'success', message: data.message })
        this.closeModal()
        await this.load()
      } catch (e) {
        this.$message({ type: 'error', message: e.message })
      } finally {
        this.saving = false
      }
    },

    async activate(id) {
      this.saving = true
      try {
        const res  = await fetch(`${API}/${id}/activate`, { method: 'PATCH' })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Erro ao ativar')
        this.$message({ type: 'success', message: data.message })
        await this.load()
      } catch (e) {
        this.$message({ type: 'error', message: e.message })
      } finally {
        this.saving = false
      }
    },

    confirmDelete(cfg) { this.deleteTarget = cfg },

    async doDelete() {
      this.saving = true
      try {
        const res  = await fetch(`${API}/${this.deleteTarget.id}`, { method: 'DELETE' })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Erro ao deletar')
        this.$message({ type: 'success', message: data.message })
        this.deleteTarget = null
        await this.load()
      } catch (e) {
        this.$message({ type: 'error', message: e.message })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
/* ══════════════════════════════════════
   AWS Config — dark navy
   ══════════════════════════════════════ */
.ac-wrap {
  display: flex; flex-direction: column; gap: 20px;
  background: #0D1B2E; min-height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
  color: #e2e8f0;
}

/* ── Header ── */
.ac-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px 14px; border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-wrap: wrap; gap: 12px;
}
.ac-header-left { display: flex; align-items: center; gap: 12px; }
.ac-header-icon {
  width: 38px; height: 38px; border-radius: 10px;
  background: rgba(245,185,53,0.1); border: 1px solid rgba(245,185,53,0.25);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ac-header-title { font-size: 15px; font-weight: 800; color: #f1f5f9; }
.ac-header-sub   { font-size: 11px; color: #64748b; margin-top: 1px; }

.ac-btn-new {
  display: flex; align-items: center; gap: 7px;
  padding: 9px 18px; border-radius: 10px; border: none;
  background: linear-gradient(135deg, #E86A10, #F5B935);
  color: #fff; font-size: 12.5px; font-weight: 800;
  cursor: pointer; transition: opacity 0.2s;
}
.ac-btn-new:hover { opacity: 0.88; }
.ac-btn-new--sm { margin-top: 12px; }

/* ── Banner ── */
.ac-banner {
  margin: 0 24px;
  display: flex; align-items: flex-start; gap: 9px;
  background: rgba(245,185,53,0.07);
  border: 1px solid rgba(245,185,53,0.2);
  border-radius: 10px; padding: 11px 14px;
  font-size: 12.5px; color: #94a3b8; line-height: 1.5;
}
.ac-banner svg { flex-shrink: 0; margin-top: 1px; }
.ac-banner strong { color: #F5B935; }

/* ── Loading / error ── */
.ac-loading {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 48px; color: #64748b; font-size: 13px;
}
.ac-spinner {
  width: 20px; height: 20px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.1);
  border-top-color: #E86A10;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.ac-error {
  display: flex; align-items: center; gap: 8px;
  margin: 0 24px; padding: 12px 16px; border-radius: 10px;
  background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.2);
  color: #f87171; font-size: 13px;
}

/* ── Empty ── */
.ac-empty {
  display: flex; flex-direction: column; align-items: center;
  padding: 56px 20px; gap: 10px; color: #475569; font-size: 14px;
}

/* ── Table ── */
.ac-table-wrap { margin: 0 24px 24px; overflow: auto; }
.ac-table {
  width: 100%; border-collapse: collapse;
  font-size: 13px;
}
.ac-table thead tr {
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.ac-table th {
  padding: 10px 14px; text-align: left;
  font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.8px; color: #475569; white-space: nowrap;
}
.ac-table td {
  padding: 13px 14px; border-bottom: 1px solid rgba(255,255,255,0.04);
  vertical-align: middle;
}
.ac-table tbody tr:hover { background: rgba(255,255,255,0.02); }
.ac-row-active { background: rgba(16,185,129,0.04) !important; }
.ac-row-active td { border-bottom-color: rgba(16,185,129,0.08); }

.ac-td-label .ac-label-text { font-weight: 700; color: #f1f5f9; }
.ac-code {
  font-family: 'Courier New', monospace; font-size: 12px;
  background: rgba(255,255,255,0.05); border-radius: 5px;
  padding: 2px 6px; color: #94a3b8;
}
.ac-masked { color: #64748b; }
.ac-badge-info {
  background: rgba(59,130,246,0.1); color: #60a5fa;
  border: 1px solid rgba(59,130,246,0.2);
  border-radius: 5px; padding: 2px 8px; font-size: 11px; font-weight: 600;
}
.ac-badge-active {
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(16,185,129,0.1); color: #10B981;
  border: 1px solid rgba(16,185,129,0.25);
  border-radius: 20px; padding: 3px 8px; font-size: 11px; font-weight: 700;
}
.ac-badge-inactive {
  background: rgba(255,255,255,0.05); color: #475569;
  border-radius: 20px; padding: 3px 8px; font-size: 11px; font-weight: 600;
}

/* ── Actions ── */
.ac-actions { display: flex; align-items: center; gap: 6px; }
.ac-btn-act {
  display: flex; align-items: center; gap: 5px;
  padding: 5px 10px; border-radius: 7px; border: 1px solid transparent;
  font-size: 11.5px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.ac-btn-activate {
  background: rgba(16,185,129,0.1); color: #10B981;
  border-color: rgba(16,185,129,0.2);
}
.ac-btn-activate:hover { background: rgba(16,185,129,0.18); }
.ac-btn-edit {
  background: rgba(59,130,246,0.1); color: #3B82F6;
  border-color: rgba(59,130,246,0.2);
}
.ac-btn-edit:hover { background: rgba(59,130,246,0.18); }
.ac-btn-delete {
  background: rgba(248,113,113,0.08); color: #f87171;
  border-color: rgba(248,113,113,0.15);
}
.ac-btn-delete:hover { background: rgba(248,113,113,0.15); }

/* ══ Modal ══ */
.ac-modal-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.55); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.ac-modal {
  background: #0D2036;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 18px; width: 100%; max-width: 540px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.5);
  display: flex; flex-direction: column;
  max-height: 90vh; overflow-y: auto;
}
.ac-modal-sm { max-width: 400px; }
.ac-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px 14px;
  font-size: 14px; font-weight: 800; color: #f1f5f9;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.ac-modal-close {
  width: 30px; height: 30px; border-radius: 8px; border: none;
  background: rgba(255,255,255,0.06); color: #94a3b8;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.15s;
}
.ac-modal-close:hover { background: rgba(255,255,255,0.1); }

.ac-modal-body { padding: 20px 22px; display: flex; flex-direction: column; gap: 16px; }

.ac-field { display: flex; flex-direction: column; gap: 6px; }
.ac-label {
  font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.7px; color: #64748b;
}
.ac-req { color: #E86A10; }
.ac-hint { font-size: 11px; color: #475569; margin-top: 2px; }

.ac-input, .ac-select {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.09);
  border-radius: 10px; padding: 10px 14px;
  font-size: 13px; color: #e2e8f0; font-family: inherit; outline: none;
  width: 100%; box-sizing: border-box;
  transition: border-color 0.2s, background 0.2s;
  color-scheme: dark;
}
.ac-input:focus, .ac-select:focus { border-color: rgba(232,106,16,0.45); background: rgba(255,255,255,0.07); }
.ac-input::placeholder { color: #475569; }
.ac-input-pass-wrap { position: relative; }
.ac-input-pass { padding-right: 42px; }
.ac-pass-toggle {
  position: absolute; right: 11px; top: 50%; transform: translateY(-50%);
  width: 26px; height: 26px; border-radius: 6px; border: none;
  background: rgba(255,255,255,0.06); color: #64748b;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.ac-pass-toggle:hover { color: #94a3b8; }

.ac-row-2 { display: flex; gap: 14px; }
.ac-row-2 .ac-field { flex: 1; }
.ac-select-wrap { position: relative; }
.ac-select { appearance: none; padding-right: 34px; cursor: pointer; }
.ac-select-arrow { position: absolute; right: 11px; top: 50%; transform: translateY(-50%); pointer-events: none; }
.ac-select option { background: #0D1B2E; }

.ac-section-divider {
  margin: 18px 0 12px;
  padding-top: 14px;
  border-top: 1px solid rgba(255,255,255,0.08);
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ac-section-divider span {
  font-size: 12px;
  font-weight: 700;
  color: #f5b935;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.ac-section-divider small {
  font-size: 11px;
  color: #94a3b8;
}
.ac-hint {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #94a3b8;
}

.ac-modal-footer {
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid rgba(255,255,255,0.06);
}
.ac-btn-cancel {
  padding: 9px 18px; border-radius: 9px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.04); color: #94a3b8;
  font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.ac-btn-cancel:hover { background: rgba(255,255,255,0.08); }
.ac-btn-save {
  display: flex; align-items: center; gap: 7px;
  padding: 9px 20px; border-radius: 9px; border: none;
  background: linear-gradient(135deg, #E86A10, #F5B935);
  color: #fff; font-size: 13px; font-weight: 800;
  cursor: pointer; transition: opacity 0.2s;
}
.ac-btn-save:disabled { opacity: 0.4; cursor: not-allowed; }
.ac-btn-danger {
  display: flex; align-items: center; gap: 7px;
  padding: 9px 20px; border-radius: 9px; border: none;
  background: rgba(248,113,113,0.15); color: #f87171;
  border: 1px solid rgba(248,113,113,0.3);
  font-size: 13px; font-weight: 800; cursor: pointer; transition: all 0.15s;
}
.ac-btn-danger:hover { background: rgba(248,113,113,0.25); }
.ac-btn-danger:disabled { opacity: 0.4; cursor: not-allowed; }

.ac-confirm-body {
  display: flex; flex-direction: column; align-items: center; text-align: center; gap: 14px;
}
.ac-confirm-body p { font-size: 13.5px; color: #94a3b8; line-height: 1.6; max-width: 300px; }
.ac-confirm-body strong { color: #f1f5f9; }
.ac-confirm-actions {
  display: flex; gap: 10px; margin-top: 8px;
}

.ac-spinner-sm {
  width: 12px; height: 12px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.2);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}

@media (max-width: 700px) {
  .ac-row-2 { flex-direction: column; }
  .ac-table-wrap { margin: 0 12px 16px; }
  .ac-header { padding: 14px 16px; }
  .ac-banner { margin: 0 12px; }
}
</style>
